import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useScrollRestoration } from '@/hooks/useScrollRestoration';
import { ArrowLeft, Image, Film, Type, Mic, Archive } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getApiBaseUrl } from '@/lib/capacitor';
import { formatDate } from '@/lib/utils';

interface Post {
  _id: string;
  contentType: 'photo' | 'video' | 'hudText' | 'voiceNote';
  caption?: string;
  hudText?: string;
  originalFilename?: string;
  processedFilename?: string;
  viewCount: number;
  user: {
    name: string;
  };
  createdAt: string;
}

const contentTypeIcons = {
  photo: Image,
  video: Film,
  hudText: Type,
  voiceNote: Mic,
};

export default function ArchivePage() {
  // Restore scroll position when returning to this page
  useScrollRestoration('archive');

  const [posts, setPosts] = useState<Post[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await fetch('/api/posts/archived');
      const data = await response.json();
      if (data.success) {
        setPosts(data.posts);
      }
    } catch (error) {
      console.error('Failed to fetch archived posts:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getMediaUrl = (filename?: string) => {
    if (!filename) return null;
    return `${getApiBaseUrl()}/uploads/${filename}`;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-10 glass border-b border-border">
        <div className="max-w-2xl mx-auto px-4 h-16 flex items-center gap-4">
          <Link to="/share">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <Archive className="w-5 h-5 text-muted-foreground" />
          <h1 className="font-display font-semibold">Archive</h1>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-2xl mx-auto p-4">
        {posts.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <Archive className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No archived posts</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {posts.map((post) => {
              const Icon = contentTypeIcons[post.contentType];
              const mediaUrl = getMediaUrl(
                post.processedFilename || post.originalFilename
              );

              return (
                <div
                  key={post._id}
                  className="rounded-xl border border-border bg-card overflow-hidden group"
                >
                  {/* Thumbnail */}
                  <div className="aspect-square bg-muted relative">
                    {post.contentType === 'photo' && mediaUrl && (
                      <img
                        src={mediaUrl}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                    )}
                    {post.contentType === 'video' && mediaUrl && (
                      <video
                        src={mediaUrl}
                        className="w-full h-full object-cover"
                        muted
                      />
                    )}
                    {(post.contentType === 'hudText' || post.contentType === 'voiceNote') && (
                      <div className="w-full h-full flex items-center justify-center">
                        <Icon className="w-8 h-8 text-muted-foreground" />
                      </div>
                    )}

                    {/* Overlay */}
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <div className="text-center text-white text-xs">
                        <p className="font-medium">{post.user.name}</p>
                        <p className="text-white/60 mt-1">
                          {formatDate(post.createdAt)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}

